import { useInspectorContext } from "@app-context/inspector/context";
import { useRefCallback } from "@enfusion-ui/hooks";
import { errorToast } from "@enfusion-ui/web-core";
import * as React from "react";

import { useNetworkCalls } from "./context";

export const useFetchCalls = () => {
  const { getSecureToken, getOEMSSessionToken, environment } =
    useInspectorContext();
  const { fetchCall: fetchCallInternal } = useNetworkCalls();
  const [state, setState] = React.useState<{
    loading: boolean;
    error: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: [any, Response] | null;
  }>({ loading: false, error: null, response: null });

  const abortControllerRef = React.useRef(new AbortController());

  const clear = useRefCallback(() => {
    try {
      abortControllerRef.current?.abort();
      setState({ loading: false, error: null, response: null });
    } catch (_err) {
      // noop
    }
  }, []);

  const performCall = useRefCallback(
    async <T = string>(
      endpoint: string,
      params: {
        method?: string;
        headers?: Record<string, string>;
        body?: Record<string, unknown>;
        onResponse?: (res: [T, Response]) => void;
      }
    ) => {
      try {
        setState((s) => ({ ...s, loading: true, error: null, response: null }));
        const secureToken = await getSecureToken();
        const appSessionToken = await getOEMSSessionToken();
        abortControllerRef.current = new AbortController();
        const res = await fetchCallInternal<T>(environment, endpoint, {
          method: params.method,
          body: params.body,
          headers: {
            ...params.headers,
            Authorization: `Bearer ${secureToken}`,
            "X-Auth-Token": appSessionToken,
          },
          signal: abortControllerRef.current.signal,
        });
        setState((s) => ({ ...s, response: res }));
        params?.onResponse?.(res);
      } catch (err) {
        setState((s) => ({ ...s, error: (err as Error).message }));
        errorToast("Failed to send request", (err as Error).message);
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [environment]
  );

  return {
    ...state,
    abortController: abortControllerRef.current,
    clear,
    call: performCall,
  };
};
